import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCourses, useScenarios, useSimSummary } from "../api/queries";
import type { ScenarioStatus } from "../api/types";
import { ScenarioActions } from "../components/SimPatient";
import { ActivityPanel, DotStatus, FilterButton, PillTabs, type Tab } from "../components/sim/ui";
import { FilterIcon, PlusIcon } from "../components/icons";
import { pct, SCENARIO_TONE, STATUS_LABELS } from "../lib/sim";
import {
  BrandLinkButton,
  CoursePageHeader,
  DataTable,
  EmptyState,
  HeadCell,
  NumberPager,
  RowCell,
  SearchInput,
  SelectInput,
  StatTile,
} from "../components/courses/ui";
import { ErrorBanner, Loading, RelativeTime } from "../components/ui";

const PAGE_SIZE = 50;
const TABS: (ScenarioStatus | "all")[] = ["all", "draft", "published", "archived"];

// ?status=draft|published|archived, ?course=<moodle id>
export function SimPatientsPage() {
  const [params, setParams] = useSearchParams();
  const status = (TABS.includes(params.get("status") as ScenarioStatus) ? params.get("status") : "all") as ScenarioStatus | "all";
  const courseParam = params.get("course");
  const courseMoodleId = courseParam ? Number(courseParam) : undefined;
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  const update = (next: { status?: string; course?: string }) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
    setOffset(0);
  };

  const summary = useSimSummary({ courseMoodleId });
  const courses = useCourses();
  const scenarios = useScenarios({
    status: status === "all" ? undefined : status,
    courseMoodleId,
    q: q.trim() || undefined,
    offset,
    limit: PAGE_SIZE,
  });
  const s = summary.data;
  const rows = scenarios.data?.scenarios ?? [];
  const total = scenarios.data?.total;
  const counts = s?.scenarios_by_status;

  return (
    <div className="px-8 py-10">
      <CoursePageHeader
        title="Simulated patients"
        subtitle="Clinical cases learners interview and are scored on. Learners can only start published scenarios."
        actions={
          <>
            <BrandLinkButton to="/sim-attempts" className="gap-2">
              <FilterIcon className="h-5 w-5" />
              All Attempts
            </BrandLinkButton>
            <Link
              to="/sim-patients/new"
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-md bg-[#0B5785] px-5 text-[15px] font-medium text-white shadow-sm transition-colors hover:bg-[#0a4a71]"
            >
              <PlusIcon className="h-5 w-5" />
              Generate Case
            </Link>
          </>
        }
      />
      <ErrorBanner error={summary.error} />

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <div className="grid grid-cols-2 gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-[0_1px_3px_rgba(16,24,40,0.06)]">
          {!s ? <div className="col-span-2 py-6"><Loading /></div> : (
            <>
              <StatTile label="Published" value={s.scenarios_by_status.published} hint={`${s.scenarios_by_status.draft} draft · ${s.scenarios_by_status.archived} archived`} />
              <StatTile label="Attempts" value={s.attempts.started.toLocaleString()} hint={`${s.attempts.unique_learners} learners`} />
              <StatTile label="Completion rate" value={pct(s.attempts.completion_rate)} hint={`${s.attempts.in_progress} in progress · ${s.attempts.abandoned} abandoned`} />
              <StatTile label="Avg score" value={pct(s.attempts.avg_score_pct)} hint="completed attempts" />
            </>
          )}
        </div>
        <div className="lg:col-span-2">
          {!s ? (
            <div className="h-full rounded-xl border border-gray-100 bg-white p-5"><Loading /></div>
          ) : (
            <ActivityPanel daily={s.daily} />
          )}
        </div>
      </div>

      <div className="mt-8">
        <h2 className="mb-3 text-sm font-medium text-gray-500">
          {total !== undefined ? `${total.toLocaleString()} scenarios` : "Scenarios"}
        </h2>
        <div className="mb-4">
          <PillTabs
            value={status}
            onChange={(t) => update({ status: t === "all" ? undefined : t })}
            tabs={TABS.map((t): Tab<ScenarioStatus | "all"> => ({
              key: t,
              label: t === "all" ? "All" : STATUS_LABELS[t],
              count: t === "all" ? (counts ? counts.draft + counts.published + counts.archived : undefined) : counts?.[t],
            }))}
          />
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-4">
          <SearchInput
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search ..."
            className="min-w-[240px] flex-1 lg:max-w-[790px]"
          />
          <div className="relative lg:ml-auto">
            <FilterButton open={showFilters} onToggle={() => setShowFilters((v) => !v)} />
            {showFilters && (
              <div className="absolute right-0 z-20 mt-2 w-72 space-y-3 rounded-lg border border-gray-100 bg-white p-4 text-sm shadow-lg">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-gray-500">Course</span>
                  <SelectInput
                    aria-label="Course"
                    value={courseMoodleId ?? ""}
                    onChange={(e) => update({ course: e.target.value || undefined })}
                  >
                    <option value="">All courses</option>
                    {(courses.data?.courses ?? []).map((c) => <option key={c.moodle_id} value={c.moodle_id}>{c.fullname}</option>)}
                  </SelectInput>
                </label>
                {courseMoodleId !== undefined && (
                  <button className="text-xs font-medium text-[#0B5785] hover:underline" onClick={() => update({ course: undefined })}>
                    Clear course filter
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <ErrorBanner error={scenarios.error} />
        {scenarios.isLoading ? <Loading /> : !rows.length ? (
          <EmptyState>
            {status === "all" && !q && !courseMoodleId
              ? <>No scenarios yet. <Link to="/sim-patients/new" className="font-medium text-[#0B5785] hover:underline">Generate the first case from a course.</Link></>
              : "No scenarios match these filters."}
          </EmptyState>
        ) : (
          <div className="overflow-hidden rounded-lg bg-white">
            <DataTable
              minWidth={1000}
              head={
                <>
                  <HeadCell>Scenario</HeadCell>
                  <HeadCell>Points</HeadCell>
                  <HeadCell>Attempts</HeadCell>
                  <HeadCell>Avg score</HeadCell>
                  <HeadCell>Last attempt</HeadCell>
                  <HeadCell>Status</HeadCell>
                  <HeadCell>Updated</HeadCell>
                  <HeadCell />
                </>
              }
            >
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-black/[0.02]">
                  <RowCell>
                    <Link to={`/sim-patients/${r.id}`} className="font-medium text-gray-900 hover:text-[#0B5785] hover:underline">{r.title}</Link>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {r.course_name ?? "No course"} · {r.difficulty} · v{r.version}
                    </div>
                  </RowCell>
                  <RowCell className="whitespace-nowrap tabular-nums">
                    {r.decision_point_count} <span className="text-gray-400">/ {r.total_max_score} pts</span>
                  </RowCell>
                  <RowCell className="tabular-nums">
                    {r.attempt_count ? (
                      <Link to={`/sim-attempts?scenario=${r.id}`} className="font-medium text-[#0B5785] hover:underline">
                        {r.completed_count}/{r.attempt_count}
                      </Link>
                    ) : "0"}
                  </RowCell>
                  <RowCell className="tabular-nums">{pct(r.avg_score_pct)}</RowCell>
                  <RowCell>{r.last_attempt_at ? <RelativeTime iso={r.last_attempt_at} /> : <span className="text-gray-400">—</span>}</RowCell>
                  <RowCell><DotStatus tone={SCENARIO_TONE[r.status] ?? "gray"} label={STATUS_LABELS[r.status]} /></RowCell>
                  <RowCell><RelativeTime iso={r.updated_at} /></RowCell>
                  <RowCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {r.status !== "archived" && (
                        <Link to={`/sim-patients/${r.id}/edit`} className="rounded-md px-2.5 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Edit</Link>
                      )}
                      <ScenarioActions scenario={r} compact only={["publish", "unpublish", "duplicate"]} />
                    </div>
                  </RowCell>
                </tr>
              ))}
            </DataTable>
          </div>
        )}
        <NumberPager
          page={Math.floor(offset / PAGE_SIZE)}
          pages={total !== undefined ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : undefined}
          hasNext={total !== undefined ? offset + rows.length < total : rows.length === PAGE_SIZE}
          onChange={setOffset}
        />
      </div>
    </div>
  );
}
